import { useEffect, useRef, useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { certificatePng } from "./MilestoneCertificate";

// Colorful 1080x1080 shareable "Practice Month" card: the child's 28-day
// warmup grid with milestone stars — native-share or download for the
// family chat. Square format for WhatsApp & Instagram.
const CELL = 88;
const GAP = 18;
const GRID_X = (1080 - (7 * CELL + 6 * GAP)) / 2;
const GRID_Y = 340;

// Which practice month: daily math warmups or speech sound practice.
const SURFACES = {
  math: { kicker: "MATH PRACTICE MONTH", noun: "math",
          button: "Share math month", file: "math-practice-month.png" },
  speech: { kicker: "SPEECH PRACTICE MONTH", noun: "speech",
            button: "Share speech month", file: "speech-practice-month.png" },
  story: { kicker: "STORY PRACTICE MONTH", noun: "story time",
           button: "Share story month", file: "story-practice-month.png" },
};

const MonthSVG = ({ svgRef, name, days, practicedCount, kicker, note }) => (
  <svg ref={svgRef} viewBox="0 0 1080 1080" xmlns="http://www.w3.org/2000/svg"
    role="img" aria-label="Practice month card">
    <defs>
      <radialGradient id="pmbg" cx="50%" cy="30%" r="85%">
        <stop offset="0%" stopColor="#312E81" />
        <stop offset="100%" stopColor="#0F0A2E" />
      </radialGradient>
      <linearGradient id="pmcell" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FCD34D" />
        <stop offset="100%" stopColor="#F97316" />
      </linearGradient>
    </defs>
    <rect width="1080" height="1080" fill="url(#pmbg)" />
    <text x="540" y="128" textAnchor="middle" fill="#FBBF24"
      fontFamily="Arial, sans-serif" fontSize="34" fontWeight="bold" letterSpacing="10">
      {kicker}
    </text>
    <text x="540" y="215" textAnchor="middle" fill="#FFFFFF"
      fontFamily="Georgia, serif" fontSize={name.length > 12 ? 52 : 64} fontWeight="bold">
      {name}'s practice month
    </text>
    <text x="540" y="275" textAnchor="middle" fill="#C7D2FE"
      fontFamily="Arial, sans-serif" fontSize="32">
      {practicedCount} practice day{practicedCount === 1 ? "" : "s"} in the last 4 weeks
    </text>
    {days.map((d, i) => {
      const x = GRID_X + (i % 7) * (CELL + GAP);
      const y = GRID_Y + Math.floor(i / 7) * (CELL + GAP);
      return (
        <g key={d.date}>
          <rect x={x} y={y} width={CELL} height={CELL} rx="20"
            fill={d.practiced ? "url(#pmcell)" : "#1E1B4B"}
            stroke={d.practiced ? "#FDBA74" : "#3730A3"} strokeWidth="3" />
          {d.milestone && (
            <text x={x + CELL / 2} y={y + CELL / 2 + 15} textAnchor="middle"
              fill="#FFFFFF" fontSize="46" fontFamily="Arial, sans-serif">
              &#9733;
            </text>
          )}
        </g>
      );
    })}
    <rect x="240" y="800" width="600" height="80" rx="40"
      fill="#FBBF24" opacity="0.14" stroke="#FBBF24" strokeWidth="2" />
    <text x="540" y="852" textAnchor="middle" fill="#FBBF24"
      fontFamily="Arial, sans-serif" fontSize="32" fontWeight="bold">
      Stars mark streak milestones
    </text>
    {note && (
      <text x="540" y="925" textAnchor="middle" fill="#FDE68A"
        fontFamily="Georgia, serif" fontSize="30" fontStyle="italic">
        &#8220;{note}&#8221;
      </text>
    )}
    <text x="540" y="985" textAnchor="middle" fill="#64748B"
      fontFamily="Arial, sans-serif" fontSize="22">
      M Speech, Language &amp; Math Helper
    </text>
  </svg>
);

export const PracticeMonthShare = ({ child, surface = "math" }) => {
  const ref = useRef(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  // Shared Card Captions: an optional short personal note baked onto the image.
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const cfg = SURFACES[surface] || SURFACES.math;

  useEffect(() => {
    const url = surface === "math"
      ? `/math/${child.child_id}/streak-calendar`
      : `/voice/streak-calendar/${child.child_id}?surface=${surface}`;
    api.get(url).then(({ data: d }) => setData(d)).catch(() => {});
  }, [child.child_id, surface]);

  const practiced = data ? data.days.filter((d) => d.practiced).length : 0;
  if (!practiced) return null;

  const share = async () => {
    setOpen(false);
    setBusy(true);
    try {
      const blob = await certificatePng(ref.current);
      const file = new File([blob], cfg.file, { type: "image/png" });
      const text = `${child.display_name} practiced ${cfg.noun} ${practiced} day${
        practiced === 1 ? "" : "s"} this month on M Speech, Language & Math Helper!`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${child.display_name}'s practice month`, text });
        toast.success("Shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = cfg.file;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Practice month downloaded — ready for the family chat");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the image");
    }
    setBusy(false);
  };

  return (
    <>
      <button type="button" disabled={busy} onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 font-bold text-[11px] px-3 py-1 hover:bg-amber-100 transition-colors disabled:opacity-60"
        data-testid={surface === "math"
          ? `share-practice-month-${child.child_id}`
          : `share-${surface}-month-${child.child_id}`}>
        <Share2 className="h-3 w-3" aria-hidden="true" />
        {busy ? "Creating…" : cfg.button}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`share-note-dialog-${surface}`}>
          <DialogHeader>
            <DialogTitle className="text-base">Add a note? (optional)</DialogTitle>
          </DialogHeader>
          <input value={note} maxLength={60} autoFocus
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && share()}
            placeholder={`So proud of you, ${child.display_name.split(" ")[0]}!`}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            aria-label="Personal note for the share image"
            data-testid={`share-note-input-${surface}`} />
          <p className="text-[11px] text-muted-foreground -mt-1">
            Your note appears on the image itself — 60 characters max.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-full"
              onClick={() => { setNote(""); share(); }}
              data-testid={`share-note-skip-${surface}`}>
              Share without note
            </Button>
            <Button size="sm" className="rounded-full" onClick={share}
              data-testid={`share-note-go-${surface}`}>
              Share
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="hidden" aria-hidden="true">
        <MonthSVG svgRef={ref} name={child.display_name}
          days={data.days} practicedCount={practiced} kicker={cfg.kicker}
          note={note.trim()} />
      </div>
    </>
  );
};
